import React from 'react';
import './Input.scss';
import classNames from 'classnames';
import { getPrefixedDataAttributes } from '@/components/utils/DomUtils';
import type { DataAttributes } from '@/components/utils/TypeUtils';

/**
 * Data passed to the `onChange` callback when a user changes the input's value.
 */
interface InputOnChangeData {
  /** Updated input value from the user */
  value: string;
}

type InputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  /** The rest of these native props have customized definitions. */
  'defaultValue' | 'onChange' | 'size' | 'type' | 'value'
> & {
  ref?: React.Ref<HTMLInputElement>;
  /** The custom class to be applied to the input. */
  className?: string;
  /** Element before the input text, within the input border */
  contentBefore?: React.ReactNode | string;
  /** Element after the input text, within the input border */
  contentAfter?: React.ReactNode | string;
  /** The custom class to be applied to the input container. */
  containerClass?: string;
  /** The input container custom styles. */
  containerStyle?: React.CSSProperties;
  /** The data-* attribute is used to store custom data private to the page or application.   */
  dataAttributes?: DataAttributes;
  /**
   * Default value of the input. Provide this if the input should be an uncontrolled component
   * which tracks its current state internally; otherwise, use `value`.
   */
  defaultValue?: string;
  /** Whether or not the input is disabled. */
  disabled?: boolean;
  /** Enable floating labels */
  floatingLabel?: boolean;
  /** The id of the input. */
  id?: string;
  /** The label(placeholder) of the input. */
  label: string;
  /** The name attribute of the input. */
  name: string;
  /**
   * Size of the input (changes the font size and spacing).
   *
   * @default 'large'
   */
  variant?: 'large' | 'sizeQuantity';
  /** Custom styles of the input element */
  style?: React.CSSProperties;
  /**
   * An input can have different text-based [types](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/Input#input_types)
   * based on the type of value the user will enter.
   *
   * Note that no custom styling is currently applied for alternative types, and some types may
   * activate browser-default styling which does not match the Fluent design language.
   *
   * (For non-text-based types such as `button` or `checkbox`, use the appropriate component or an
   * `<input>` element instead.)
   * @default 'text'
   */
  type?:
    | 'text'
    | 'email'
    | 'password'
    | 'search'
    | 'tel'
    | 'url'
    | 'date'
    | 'datetime-local'
    | 'month'
    | 'number'
    | 'time'
    | 'week';
  /**
   * Current value of the input. Provide this if the input is a controlled component where you
   * are maintaining its current state; otherwise, use `defaultValue`.
   */
  value?: string;
  /**
   * Called when the user changes the input's value.
   */
  onChange?: (ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => void;
  /**
   * Called when the user presses a key.
   */
  onKeyDown?: (ev: React.KeyboardEvent<HTMLInputElement>) => void;
  /**
   * Called when the user focuses out of the input.
   */
  onBlur?: (ev: React.FocusEvent<HTMLInputElement>) => void;
  /** Content after div click handler */
  onContentAfterClick?: (ev: React.SyntheticEvent) => void;
  /** Field level error message */
  fieldErrMessg?: string;
  /** Field level error message */
  fieldErrMessgClass?: string;
  /** Called when the user focuses of the input. */
  onFocus?: (ev: React.FocusEvent<HTMLInputElement>) => void;
  /** Called when the user start typing a value in the input. */
  onInput?: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  /** Called when the user rotate the mouse wheel to scroll. */
  onWheel?: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  step?: string;
};

/** Input is a form element that accepts a single line of text. */
const Input = React.forwardRef<HTMLInputElement, InputProps>((props: InputProps, ref) => {
  const {
    id,
    name,
    label,
    style,
    value,
    disabled,
    className,
    contentBefore,
    contentAfter,
    dataAttributes,
    defaultValue,
    floatingLabel,
    containerStyle,
    containerClass,
    variant = 'large',
    type = 'text',
    onChange,
    onKeyDown,
    onBlur,
    onContentAfterClick,
    onFocus,
    onInput,
    onWheel,
    fieldErrMessg,
    fieldErrMessgClass,
    ...rest
  } = props;

  const [inputValue, setInputValue] = React.useState(defaultValue ?? value ?? '');

  /**
   * Update the input's value when the user changes it.
   */
  const handleChange = React.useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = ev.target.value;
      setInputValue(newValue);
      onChange?.(ev, { value: newValue });
    },
    [onChange]
  );

  /**
   * Handle the keydown event and pass the event object to the parent component.
   */
  const handleKeyDown = React.useCallback(
    (ev: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(ev);
    },
    [onKeyDown]
  );

  /**
   * Handle the blur event and pass the event object to the parent component.
   */
  const handleBlur = React.useCallback(
    (ev: React.FocusEvent<HTMLInputElement>) => {
      onBlur?.(ev);
    },
    [onBlur]
  );

  /**
   * Handle the blur event and pass the event object to the parent component.
   */
  const handleFocus = React.useCallback(
    (ev: React.FocusEvent<HTMLInputElement>) => {
      onFocus?.(ev);
    },
    [onFocus]
  );

  /**
   * Handle the input event and pass the event object to the parent component.
   */
  const handleInput = React.useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      onInput?.(ev);
    },
    [onInput]
  );

  /**
   * Handle the mouse wheel scroll and pass the event object to the parent component.
   */
  const handleWheel = React.useCallback(
    (ev: React.WheelEvent<HTMLInputElement>) => {
      onWheel?.(ev);
    },
    [onWheel]
  );

  const inputClass = classNames(className, {
    'input-control': true,
    [`input-control--${variant}`]: variant,
    'input-control--disabled': disabled,
    'input-control--floating-input': floatingLabel,
    [`input-control--content-before-${variant}`]: contentBefore,
    [`input-control--content-after-${variant}`]: contentAfter,
  });
  const labelClass = classNames('input-label', className, {
    filled: value ?? inputValue,
  });
  const inputContainerClass = classNames('input-container', containerClass, {
    'floating-input-container': floatingLabel,
    [`input-container--${variant}`]: variant,
    [`input-container--disabled`]: disabled,
  });

  const inputName = `input-${type}-${name}`;
  const inputId = id ?? inputName;

  return (
    <>
      <div
        className={`${inputContainerClass} ${
          fieldErrMessg && !fieldErrMessgClass ? 'input-error' : ''
        }   ${fieldErrMessg && fieldErrMessgClass ? `${fieldErrMessgClass}-input-error` : ''} `}
        style={containerStyle}
      >
        {contentBefore && (
          <div
            className={`input-container__content-before input-container__content-before--${variant}`}
          >
            {contentBefore}
          </div>
        )}

        <input
          ref={ref}
          type={type}
          id={inputId}
          name={inputName}
          className={inputClass}
          value={value ?? inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onInput={handleInput}
          onWheel={handleWheel}
          style={style}
          autoComplete="off"
          placeholder={floatingLabel ? '' : label}
          {...getPrefixedDataAttributes(dataAttributes, 'Input')}
          {...rest}
        />
        {/** Inputs with floating labels will only see this label. */}
        {floatingLabel && (
          <label className={labelClass} htmlFor={inputName}>
            {label}
          </label>
        )}

        {contentAfter && (
          <button
            type="button"
            className={`input-container__content-after input-container__content-after--${variant}`}
            onClick={(e) => onContentAfterClick && onContentAfterClick(e)}
            tabIndex={0}
          >
            {contentAfter}
          </button>
        )}
      </div>
      <div role="alert" className={`err-messg-container ${fieldErrMessgClass || ''}`}>
        {fieldErrMessg}
      </div>
    </>
  );
});

Input.displayName = 'Input';

Input.defaultProps = {
  variant: 'large',
  type: 'text',
  floatingLabel: false,
};

export default Input;
